import { createSelector } from "@reduxjs/toolkit";
import {
    findChainSuccessor,
    hasDateOverlap,
    isPriceHistoryEntryConnectedToActivePeriod,
    validateNoGap,
} from "components/Title/PriceHistory/utils";
import type { RootState } from "store/store";

// Selectors on titles.title.overriddenChannelsData entries
const selectAllChannelsPriceHistory = (state: RootState) =>
    state.titles.title?.overriddenChannelsData?.filter((c) => !c.countryMarket).flatMap((c) => c.priceHistory ?? []) ??
    [];
const selectDefaultPriceHistory = (state: RootState) => state.titles.title?.defaultPriceHistory ?? [];
export const selectAllPriceHistory = createSelector(
    [selectAllChannelsPriceHistory, selectDefaultPriceHistory],
    (a, b) => [...a, ...b],
);
export const selectPriceHistoryForChannel = (channelId: number | undefined) =>
    createSelector(selectAllPriceHistory, (state) => state.filter((c) => c.channelId === channelId));

/** Selectors on draft entries : edit mode */
export const selectDraftPriceHistoryForChannel = (channelId: number | undefined) =>
    createSelector(
        (state: RootState) => state.priceHistory.draftEntries,
        (state) => state.filter((e) => e.channelId === channelId),
    );
export const selectChannelAllActivePriceHistory = (
    channelId: number | undefined,
    periodStart: string | Date | undefined,
    periodEnd: string | Date | undefined,
) =>
    createSelector(selectDraftPriceHistoryForChannel(channelId), (state) =>
        state.filter((e) => isPriceHistoryEntryConnectedToActivePeriod(e, { periodStart, periodEnd })),
    );

/* Only the last period of its chain (the one no other period follows) can be
 * deleted, and never when it is the channel's only period — earlier periods are
 * adjusted through their end dates instead. */
export const isPriceHistoryDeletable = (channelId: number | undefined, entryId: number) =>
    createSelector(selectDraftPriceHistoryForChannel(channelId), (entries) => {
        const entry = entries.find((e) => e.id === entryId);
        if (!entry || entries.length <= 1) return false;
        return !findChainSuccessor(entries, entry);
    });

// Error selectors
export const selectHasDateGapError = (channelId: number | undefined) =>
    createSelector(selectDraftPriceHistoryForChannel(channelId), (state) => validateNoGap(state, channelId));
export const selectHasNoActiveEntryError = (channelId: number | undefined) =>
    createSelector(
        (state: RootState) =>
            selectChannelAllActivePriceHistory(
                channelId,
                state.period.activePeriodData?.periodStart,
                state.period.activePeriodData?.periodEnd,
            )(state),
        (state: RootState) => state.period.activePeriodData,
        (activeEntries, periodData) => {
            if (activeEntries.length === 0 || !periodData) return true;
            const overlapPeriodStart = activeEntries.some((e) =>
                hasDateOverlap(
                    e.startDate ? new Date(e.startDate) : null,
                    e.endDate ? new Date(e.endDate) : null,
                    null,
                    periodData.periodStart,
                ),
            );
            const overlapPeriodEnd = activeEntries.some((e) =>
                hasDateOverlap(
                    e.startDate ? new Date(e.startDate) : null,
                    e.endDate ? new Date(e.endDate) : null,
                    periodData.periodEnd,
                    null,
                ),
            );
            // Entries needs to overlap the start and the end of the period
            return !overlapPeriodStart || !overlapPeriodEnd;
        },
    );
export const selectHasNoFutureEntryError = (channelId: number | undefined) =>
    createSelector(
        (state: RootState) => selectDraftPriceHistoryForChannel(channelId)(state).filter((e) => !e.endDate),
        (state) => state.length === 0,
    );
